import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert, BreadcrumbGroup, Container, Header, SpaceBetween, Wizard,
} from '@cloudscape-design/components';
import { apiFetch } from './apiFetch';
import {
  createFmBundle, deriveSlug, FmCatalog, hasFmProvisioningCreds,
  getFmModelCatalog, MissingCredsError, syncFmModelCatalog,
} from './fmBundles';
import BundleInfoStep from './fmBundleWizard/BundleInfoStep';
import ModelsStep from './fmBundleWizard/ModelsStep';
import ComputeRolesStep from './fmBundleWizard/ComputeRolesStep';
import BudgetStep from './fmBundleWizard/BudgetStep';
import ReviewStep from './fmBundleWizard/ReviewStep';
import {
  Errors, FmWizardState, INITIAL_STATE, NEW_ENV_VALUE,
} from './fmBundleWizard/types';
import { validateStep } from './fmBundleWizard/validation';

interface ProjectOption { documentId: string; name: string; }
interface EnvOption { documentId: string; name: string; isProduction: boolean; }

export default function CreateFmBundlePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialProjectId = searchParams.get('projectId') ?? '';

  const [activeStep, setActiveStep] = useState(0);
  const [state, setState] = useState<FmWizardState>(INITIAL_STATE);
  const [errors, setErrors] = useState<Errors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [credsMissing, setCredsMissing] = useState<string | null>(null);

  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [environments, setEnvironments] = useState<EnvOption[]>([]);
  const [envLoading, setEnvLoading] = useState(false);

  const [catalog, setCatalog] = useState<FmCatalog | null>(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [vendor, setVendor] = useState('');
  const [catalogWarning, setCatalogWarning] = useState<string | null>(null);

  const patch = useCallback((p: Partial<FmWizardState>) => setState((prev) => ({ ...prev, ...p })), []);

  // Load projects + catalog on mount.
  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch('/projects');
        const json = await res.json();
        const list: ProjectOption[] = (json?.data ?? []).map((p: any) => ({
          documentId: p.documentId, name: p.name,
        }));
        setProjects(list);
      } catch { /* non-fatal */ }
    })();
    (async () => {
      setCatalogLoading(true);
      try {
        const c = await getFmModelCatalog(true);
        setCatalog(c);
        if (c.providers.length) setVendor(c.providers[0].provider);
      } catch { /* non-fatal */ } finally {
        setCatalogLoading(false);
      }
    })();
  }, []);

  const loadEnvironments = useCallback(async (projectId: string) => {
    setEnvLoading(true);
    try {
      const populate = 'populate[environments][fields][0]=name&populate[environments][fields][1]=isProduction';
      const res = await apiFetch(`/projects/${encodeURIComponent(projectId)}?${populate}`);
      const json = await res.json();
      const envs: EnvOption[] = (json?.data?.environments ?? []).map((e: any) => ({
        documentId: e.documentId, name: e.name, isProduction: !!e.isProduction,
      }));
      setEnvironments(envs);
    } catch {
      setEnvironments([]);
    } finally {
      setEnvLoading(false);
    }
  }, []);

  const onProjectChange = useCallback((documentId: string) => {
    const project = projects.find((p) => p.documentId === documentId);
    const name = project?.name ?? '';
    const slug = deriveSlug(name);
    setState((prev) => ({
      ...prev,
      projectId: documentId,
      projectName: name,
      environmentId: '',
      // Service name follows the slug until the user edits it.
      serviceName: prev.serviceNameTouched ? prev.serviceName : slug,
    }));
    loadEnvironments(documentId);
  }, [projects, loadEnvironments]);

  // Pre-select project from the query param once projects are loaded.
  useEffect(() => {
    if (initialProjectId && projects.length && !state.projectId) {
      onProjectChange(initialProjectId);
    }
  }, [initialProjectId, projects, state.projectId, onProjectChange]);

  const onRefreshCatalog = useCallback(async () => {
    setRefreshing(true);
    setCatalogWarning(null);
    try {
      await syncFmModelCatalog();
      const c = await getFmModelCatalog(true);
      setCatalog(c);
      if (c.providers.length && !c.providers.some((p) => p.provider === vendor)) {
        setVendor(c.providers[0].provider);
      }
    } catch (e: any) {
      setCatalogWarning(e.message);
    } finally {
      setRefreshing(false);
    }
  }, [vendor]);

  const handleSubmit = useCallback(async () => {
    setSubmitError(null);
    if (!(await hasFmProvisioningCreds())) {
      setCredsMissing(
        'AWS credentials for the FM provisioning account (428127313411) are not available. Add them in Settings.',
      );
      return;
    }
    setCredsMissing(null);
    setSubmitting(true);
    try {
      const body: Parameters<typeof createFmBundle>[0] = {
        projectId: state.projectId,
        team: state.team,
        serviceName: state.serviceName,
        models: state.models.map((m) => ({ shortName: m.shortName, modelId: m.modelId, provider: m.provider })),
        computeRoles: state.computeRoles.map((r) => r.trim()).filter(Boolean),
        businessEmails: state.businessEmails.map((e) => e.trim()).filter(Boolean),
      };
      if (state.budgetPeriod === 'monthly') body.monthlyBudgetUSD = Number(state.budgetAmount);
      else body.yearlyBudgetUSD = Number(state.budgetAmount);
      if (state.environmentId === NEW_ENV_VALUE) {
        body.newEnvironment = { name: state.newEnvName.trim(), isProduction: state.newEnvIsProduction };
      } else {
        body.environmentId = state.environmentId;
      }
      const result = await createFmBundle(body);
      navigate(`/products/fm_bundle/${result.bundle.documentId}`, { state: { dryRun: result.dryRun } });
    } catch (e: any) {
      if (e instanceof MissingCredsError) setCredsMissing(e.message);
      else setSubmitError(e.message);
    } finally {
      setSubmitting(false);
    }
  }, [state, navigate]);

  const onCancel = () =>
    navigate(state.projectId ? `/projects/${state.projectId}` : '/create');

  const goToStep = (requested: number) => {
    // Block moving forward past an invalid step.
    if (requested > activeStep) {
      const stepErrors = validateStep(activeStep, state, environments);
      if (Object.keys(stepErrors).length) {
        setErrors(stepErrors);
        return;
      }
    }
    setErrors({});
    setActiveStep(requested);
  };

  const steps = useMemo(() => ([
    {
      title: 'Bundle info',
      content: (
        <Container header={<Header variant="h2">Bundle info</Header>}>
          <BundleInfoStep
            state={state} errors={errors} projects={projects} environments={environments}
            envLoading={envLoading} onProjectChange={onProjectChange} patch={patch}
          />
        </Container>
      ),
    },
    {
      title: 'Models',
      content: (
        <Container header={<Header variant="h2">Models</Header>}>
          <SpaceBetween size="m">
            {catalogWarning && <Alert type="warning">{catalogWarning}</Alert>}
            <ModelsStep
              state={state} errors={errors} catalog={catalog} catalogLoading={catalogLoading}
              refreshing={refreshing} vendor={vendor} onVendorChange={setVendor}
              onRefreshCatalog={onRefreshCatalog} patch={patch}
            />
          </SpaceBetween>
        </Container>
      ),
    },
    {
      title: 'Compute roles',
      content: (
        <Container header={<Header variant="h2">Compute roles</Header>}>
          <ComputeRolesStep state={state} errors={errors} patch={patch} />
        </Container>
      ),
    },
    {
      title: 'Budget & notifications',
      content: (
        <Container header={<Header variant="h2">Budget & notifications</Header>}>
          <BudgetStep state={state} errors={errors} patch={patch} />
        </Container>
      ),
    },
    {
      title: 'Review & submit',
      content: (
        <ReviewStep
          state={state} environments={environments} submitError={submitError}
          credsMissing={credsMissing} onEdit={goToStep}
        />
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]), [state, errors, projects, environments, envLoading, catalog, catalogLoading,
    refreshing, vendor, catalogWarning, submitError, credsMissing]);

  return (
    <SpaceBetween size="l">
      <BreadcrumbGroup
        items={[
          { text: 'Create Product', href: '/create' },
          { text: 'FM Bundle', href: '#' },
        ]}
        onFollow={(e) => { e.preventDefault(); navigate(e.detail.href); }}
      />
      <Header variant="h1" description="Configure and submit a new Foundation Model bundle.">
        Create FM Bundle
      </Header>
      <Wizard
        i18nStrings={{
          stepNumberLabel: (n) => `Step ${n}`,
          collapsedStepsLabel: (n, total) => `Step ${n} of ${total}`,
          submitButton: 'Submit',
          previousButton: 'Previous',
          nextButton: 'Next',
          cancelButton: 'Cancel',
          optional: 'optional',
        }}
        activeStepIndex={activeStep}
        onNavigate={({ detail }) => goToStep(detail.requestedStepIndex)}
        onCancel={onCancel}
        onSubmit={handleSubmit}
        isLoadingNextStep={submitting}
        steps={steps}
      />
    </SpaceBetween>
  );
}
